import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Modal } from './Modal';

// "Quem mexeu neste produto?" — le o MESMO endpoint da aba Logs da ficha, sem tirar o operador
// da tela onde ele esta. Nasceu na tela de Localizacoes por Produtos: durante a ronda, abrir a
// ficha inteira pra ver uma data custa a volta pro lugar certo da lista.
//
// A fonte e o LOG, nunca `produtos.atualizado_em`: aquele campo e tocado pela VENDA (que nao tem
// usuario junto) e, pior, chega 3h adiantado quando quem grava e o driver em vez do NOW() do
// Postgres — hoje ha produto com data de atualizacao no FUTURO. O log tem quem, quando e o que
// mudou, no horario certo.

interface Linha {
  id: number;
  campo: string;
  valor_anterior: string | null;
  valor_novo: string | null;
  usuario_nome: string | null;
  /** Data JA formatada pelo Postgres. Ver o porque no GET /produtos/:id/historico — o `criado_em`
   *  cru volta 3h atrasado quando o JS o interpreta, e "quando" e metade deste recurso. */
  quando: string;
}

// O nome da coluna nao e o nome que o operador usa. Sem isso a tela mostra "estoque_minimo" e
// "preco_pix" cru. Campo fora da lista cai no proprio nome — nunca some.
const NOMES: Record<string, string> = {
  contagem_lugar: 'Contagem na prateleira',
  minimo_lugar: 'Mínimo da prateleira',
  lugar: 'Prateleira',
  lugar_principal: 'Prateleira principal',
  // `localizacao` e a coluna de texto CONGELADA (DA-324). Linha antiga do log ainda usa esse nome —
  // marcar como "(antigo)" pra nao parecer que o lugar de hoje mudou.
  localizacao: 'Localização (registro antigo)',
  estoque_atual: 'Estoque',
  estoque_minimo: 'Mínimo de Compra',
  preco_pix: 'Preço PIX',
  preco_cartao: 'Preço cartão',
  preco_custo: 'Custo',
  descricao: 'Descrição',
  descricao_complementar: 'Descrição complementar',
  descricao_comercial: 'Descrição comercial',
  observacoes_produto: 'Observações',
  vantagens: 'Vantagens (catálogo)',
  ean: 'EAN',
  referencia: 'Referência',
  ncm: 'NCM',
  cest: 'CEST',
  cfop: 'CFOP',
  origem: 'Origem',
  situacao_tributaria_id: 'Situação tributária',
  unidade_venda_id: 'Unidade de venda',
  fator_conversao: 'Fator de conversão',
  peso_liquido: 'Peso líquido',
  peso_bruto: 'Peso bruto',
  codigo_interno: 'Código',
  postar_catalogo: 'Catálogo',
  uso_consumo: 'Uso e consumo',
  foto_url: 'Foto',
  status: 'Situação',
  unidade_conteudo_embalagem: 'Unid. do conteúdo',
  qtd_embalagem_padrao: 'Qtd. da embalagem',
  unidade_compra: 'Unidade de compra',
  permite_fracionado: 'Permite fracionado',
  marca_id: 'Marca',
  grupo_id: 'Grupo',
  categoria_id: 'Categoria',
};

const rotulo = (campo: string) => NOMES[campo] ?? campo;

export function ModalUltimasAlteracoes({
  produtoId,
  titulo,
  onFechar,
}: {
  produtoId: number;
  titulo: string;
  onFechar: () => void;
}) {
  const [linhas, setLinhas] = useState<Linha[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api.get<Linha[]>(`/api/produtos/${produtoId}/historico`)
      .then((r) => { if (vivo) setLinhas(r || []); })
      .catch((e: any) => { if (vivo) setErro(e.message); });
    return () => { vivo = false; };
  }, [produtoId]);

  return (
    <Modal aberto onFechar={onFechar} titulo={`Últimas alterações — ${titulo}`} largura={720}>
      {erro ? (
        <div style={{ padding: 16, color: 'var(--text-muted)' }}>{erro}</div>
      ) : linhas === null ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando...</div>
      ) : linhas.length === 0 ? (
        <div style={{ padding: 24, color: 'var(--text-muted)' }}>
          Nenhuma alteração registrada para este produto.
          <div style={{ fontSize: 12, marginTop: 8 }}>
            O log guarda o que uma pessoa mudou no cadastro. Movimento de estoque por venda não entra aqui.
          </div>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--panel-border)', textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '8px 12px 8px 0', width: 128, fontSize: 11, textTransform: 'uppercase' }}>Quando</th>
                <th style={{ padding: '8px 12px 8px 0', width: 96, fontSize: 11, textTransform: 'uppercase' }}>Quem</th>
                <th style={{ padding: '8px 12px 8px 0', width: 168, fontSize: 11, textTransform: 'uppercase' }}>O que</th>
                <th style={{ padding: '8px 12px 8px 0', fontSize: 11, textTransform: 'uppercase' }}>De → para</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((h) => (
                <tr key={h.id} style={{ borderBottom: '1px solid var(--panel-border)' }}>
                  <td className="cel-num" style={{ padding: '6px 12px 6px 0', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {h.quando}
                  </td>
                  <td style={{ padding: '6px 12px 6px 0', fontSize: 12, fontWeight: 600 }}>{h.usuario_nome || '—'}</td>
                  <td style={{ padding: '6px 12px 6px 0', fontSize: 12 }}>{rotulo(h.campo)}</td>
                  {/* De->para em cinza -> normal, NUNCA vermelho->verde: sao as duas cores que o
                      Ruan nao distingue (a aba Logs da ficha faz isso e fica ilegivel pra ele).
                      A seta ja diz a direcao; a cor nao precisa carregar significado nenhum. */}
                  <td style={{ padding: '6px 12px 6px 0', fontSize: 12 }}>
                    <span style={{ color: 'var(--text-muted)' }}>{h.valor_anterior || '—'}</span>
                    <span style={{ color: 'var(--text-muted)', margin: '0 6px' }}>→</span>
                    <span style={{ fontWeight: 600 }}>{h.valor_novo || '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
